import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Code2, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CopyButton } from '@/components/ui/copy-button';

interface JsonViewerProps {
  data: unknown;
  title?: string;
  maxHeight?: string | number;
  className?: string;
  showCopyButton?: boolean;
  defaultView?: 'readable' | 'raw';
}

export const JsonViewer: React.FC<JsonViewerProps> = ({
  data,
  title,
  maxHeight = 300,
  className,
  showCopyButton = true,
  defaultView = 'readable',
}) => {
  const [viewMode, setViewMode] = useState<'readable' | 'raw'>(defaultView);
  const jsonString = JSON.stringify(data, null, 2);

  // Helper function to render human-readable format
  const renderReadable = (obj: unknown, depth = 0): React.ReactNode => {
    if (obj === null)
      return <span className='text-muted-foreground'>null</span>;
    if (obj === undefined)
      return <span className='text-muted-foreground'>undefined</span>;

    if (typeof obj === 'string') {
      return (
        <span className='text-green-600 dark:text-green-400'>"{obj}"</span>
      );
    }

    if (typeof obj === 'number') {
      return <span className='text-blue-600 dark:text-blue-400'>{obj}</span>;
    }

    if (typeof obj === 'boolean') {
      return (
        <span className='text-purple-600 dark:text-purple-400'>
          {obj.toString()}
        </span>
      );
    }

    if (Array.isArray(obj)) {
      if (obj.length === 0)
        return <span className='text-muted-foreground'>[]</span>;

      return (
        <div className={cn('ml-4', depth === 0 && 'ml-0')}>
          <span className='text-muted-foreground'>[</span>
          {obj.map((item, index) => (
            <div key={index} className='ml-4 my-1'>
              <span className='text-muted-foreground'>{index}:</span>{' '}
              {renderReadable(item, depth + 1)}
            </div>
          ))}
          <span className='text-muted-foreground'>]</span>
        </div>
      );
    }

    if (typeof obj === 'object' && obj !== null) {
      const entries = Object.entries(obj);
      if (entries.length === 0)
        return <span className='text-muted-foreground'>{'{}'}</span>;

      return (
        <div className={cn('ml-4', depth === 0 && 'ml-0')}>
          <span className='text-muted-foreground'>{'{'}</span>
          {entries.map(([key, value], _) => (
            <div key={key} className='ml-4 my-1'>
              <span className='text-foreground font-medium'>{key}:</span>{' '}
              {renderReadable(value, depth + 1)}
            </div>
          ))}
          <span className='text-muted-foreground'>{'}'}</span>
        </div>
      );
    }

    return <span className='text-muted-foreground'>{String(obj)}</span>;
  };

  const maxHeightStyle =
    typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;

  return (
    <div className={cn('relative', className)}>
      {/* Header with controls */}
      <div className='flex items-center justify-between mb-2'>
        <div className='flex items-center gap-2 w-full'>
          {title && <span className='text-sm font-medium'>{title}</span>}
          <div className='flex items-center rounded-md border bg-muted/50 p-1 ml-auto'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setViewMode('readable')}
              aria-pressed={viewMode === 'readable'}
              className={cn(
                'h-6 px-2 text-xs',
                viewMode === 'readable'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground',
              )}
            >
              <FileText className='w-3 h-3 mr-1' />
              Readable
            </Button>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setViewMode('raw')}
              aria-pressed={viewMode === 'raw'}
              className={cn(
                'h-6 px-2 text-xs',
                viewMode === 'raw'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground',
              )}
            >
              <Code2 className='w-3 h-3 mr-1' />
              Raw JSON
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className='relative'>
        <div
          className='overflow-auto bg-muted/30 rounded-lg border p-3'
          style={{ maxHeight: maxHeightStyle }}
        >
          {viewMode === 'readable' ? (
            <div className='text-sm font-mono'>{renderReadable(data)}</div>
          ) : (
            <pre className='text-xs font-mono whitespace-pre'>{jsonString}</pre>
          )}
        </div>
        {showCopyButton && (
          <div className='right-2 top-2 absolute'>
            <CopyButton text={jsonString} size='small' ariaLabel='Copy JSON' />
          </div>
        )}
      </div>
    </div>
  );
};

// Compact version for inline use
export const JsonViewerCompact: React.FC<{
  data: unknown;
  className?: string;
}> = ({ data, className }) => {
  const [expanded, setExpanded] = useState(false);
  const preview = JSON.stringify(data);
  const isLong = preview.length > 100;

  if (!expanded) {
    return (
      <div className={cn('inline-flex items-center gap-2', className)}>
        <code className='text-xs bg-muted px-2 py-1 rounded'>
          {isLong ? preview.substring(0, 100) + '...' : preview}
        </code>
        {isLong && (
          <Button
            variant='link'
            size='sm'
            onClick={() => setExpanded(true)}
            className='h-auto p-0 text-xs'
          >
            Expand
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <JsonViewer
        data={data}
        maxHeight={200}
        defaultView='raw'
        className='mt-2'
      />
      <Button
        variant='link'
        size='sm'
        onClick={() => setExpanded(false)}
        className='h-auto p-0 text-xs mt-1'
      >
        Collapse
      </Button>
    </div>
  );
};
